import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, InputGroup, Spinner } from 'react-bootstrap';
import {
  creditsService,
  type BillingAdminRow,
  type BillingRoleHolder,
  type ClientUser,
} from '../services/creditsService';
import './BillingAdminsPanel.css';

interface Props {
  clientName: string;
}

/**
 * Raw AWS throttle errors surface as a bare "Rate exceeded", which reads like a portal bug rather
 * than a transient quota blip (BUG-469). Name the cause and tell the operator what to do.
 */
function friendlyAwsError(e: unknown): string {
  const name = e instanceof Error ? e.name : '';
  const message = e instanceof Error ? e.message : String(e);
  if (/throttl|toomanyrequests/i.test(name) || /rate exceeded/i.test(message)) {
    return 'AWS is limiting requests right now. Wait a few seconds, then try again.';
  }
  return message || 'Unknown error';
}

/**
 * Billing-admins tool (Numa Credit System). Billing admin = who may SEE credit data in-client. Two
 * kinds of person hold it:
 *  - the GRANT on the client ledger, permanent: NOT a Cognito group (any admin could self-grant
 *    that), written only Arcanum-side (here, through the client access Lambda) or by an existing billing admin
 *    in-client. This panel seeds the FIRST one per client. Every change is written to the ledger
 *    and logged to the portal activity table.
 *  - the BILLING ROLE on the client's roles table, given on the client's Users page by somebody
 *    who holds Billing either way. The portal never writes it; it is listed here, read only, so
 *    the operator sees everybody who holds Billing.
 *
 * Error handling is deliberately split three ways (BUG-469: a failed post-promote refresh used to
 * render exactly like a failed promote, while the write had in fact succeeded):
 *  - opError: the promote/revoke WRITE failed, nothing changed.
 *  - loadError: the roster read failed, the list shown may be stale, but writes stand.
 *  - usersError: the candidate picker couldn't load, previously swallowed into "No eligible users".
 */
export default function BillingAdminsPanel({ clientName }: Props) {
  const [admins, setAdmins] = useState<BillingAdminRow[]>([]);
  const [roleHolders, setRoleHolders] = useState<BillingRoleHolder[]>([]);
  const [users, setUsers] = useState<ClientUser[]>([]);
  const [selectedSub, setSelectedSub] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [opError, setOpError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [usersError, setUsersError] = useState<string | null>(null);
  /** The Billing role list could not be read; the grants above it stand. */
  const [rolesError, setRolesError] = useState<string | null>(null);
  /** The person whose Remove button was pressed and who waits for a second, confirming press. */
  const [confirmSub, setConfirmSub] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const [adminsRes, usersRes, rolesRes] = await Promise.allSettled([
      creditsService.listBillingAdmins(clientName),
      creditsService.listClientUsers(clientName),
      creditsService.listBillingRoleHolders(clientName),
    ]);
    if (adminsRes.status === 'fulfilled') {
      setAdmins(adminsRes.value);
    } else {
      setLoadError(friendlyAwsError(adminsRes.reason));
    }
    if (rolesRes.status === 'fulfilled') {
      setRoleHolders(rolesRes.value);
      setRolesError(null);
    } else {
      setRolesError(friendlyAwsError(rolesRes.reason));
    }
    if (usersRes.status === 'fulfilled') {
      setUsers(usersRes.value);
      setUsersError(null);
    } else {
      setUsersError(friendlyAwsError(usersRes.reason));
    }
    setLoading(false);
  }, [clientName]);

  useEffect(() => {
    // Reset per-client state on switch: carrying another client's user list into a failed load
    // would offer subs that don't exist in this client's pool.
    setAdmins([]);
    setRoleHolders([]);
    setUsers([]);
    setSelectedSub('');
    setNotice(null);
    setOpError(null);
    setUsersError(null);
    setRolesError(null);
    setConfirmSub(null);
    void load();
  }, [load]);

  const adminSubs = new Set(admins.map((a) => a.sub));
  const candidates = users.filter((u) => !adminSubs.has(u.sub));
  const emailOf = (sub: string): string => users.find((u) => u.sub === sub)?.email ?? `${sub.slice(0, 8)}…`;

  const promote = async () => {
    if (!selectedSub) return;
    const u = users.find((x) => x.sub === selectedSub);
    setBusy(true);
    setOpError(null);
    setNotice(null);
    try {
      await creditsService.promoteBillingAdmin(clientName, selectedSub, u?.email ?? null);
      // The write is durable from here. Anything that fails below is only a display refresh.
      setNotice(`Gave ${u?.email ?? selectedSub} permanent billing access.`);
      setSelectedSub('');
    } catch (e) {
      setOpError(`Access was not given: ${friendlyAwsError(e)}`);
      setBusy(false);
      return;
    }
    await load();
    setBusy(false);
  };

  const demote = async (sub: string) => {
    const a = admins.find((x) => x.sub === sub);
    setConfirmSub(null);
    setBusy(true);
    setOpError(null);
    setNotice(null);
    try {
      await creditsService.demoteBillingAdmin(clientName, sub);
      setNotice(`Removed permanent billing access from ${a?.email ?? sub}.`);
    } catch (e) {
      setOpError(`Access was not removed: ${friendlyAwsError(e)}`);
      setBusy(false);
      return;
    }
    await load();
    setBusy(false);
  };

  const total = admins.length + roleHolders.filter((h) => !adminSubs.has(h.sub)).length;

  return (
    <Card className="billing-admins">
      <Card.Header className="billing-admins__head">
        <span>Billing admins</span>
        {!loading && (
          <span className="billing-admins__count">
            {total} {total === 1 ? 'person' : 'people'}
          </span>
        )}
      </Card.Header>
      <Card.Body>
        <p className="billing-admins__intro">
          Only a billing admin sees credits and costs inside {clientName}. A person becomes one in two ways: permanent
          access, given here, or the Billing role, given on the client&apos;s own Users page by somebody who already has
          it. Give the first person access here.
        </p>
        {notice && <Alert variant="success">{notice}</Alert>}
        {opError && <Alert variant="danger">{opError}</Alert>}
        {loadError && (
          <Alert variant="warning" className="billing-admins__stale">
            <span>{`This list did not refresh, so it may be out of date: ${loadError}`}</span>
            <Button variant="outline-secondary" size="sm" disabled={busy || loading} onClick={() => void load()}>
              Try again
            </Button>
          </Alert>
        )}
        {loading ? (
          <div className="billing-admins__loading">
            <Spinner size="sm" animation="border" /> Loading billing admins
          </div>
        ) : (
          <>
            <h3 className="section-label">Permanent access</h3>
            {admins.length === 0 ? (
              <p className="billing-admins__empty">Nobody yet. Give the first person access below.</p>
            ) : (
              <ul className="billing-admins__list">
                {admins.map((a) => (
                  <li key={a.sub}>
                    <span className="billing-admins__who">{a.email || emailOf(a.sub)}</span>
                    {confirmSub === a.sub ? (
                      <span className="billing-admins__confirm">
                        Remove their access?
                        <Button variant="danger" size="sm" disabled={busy} onClick={() => demote(a.sub)}>
                          Remove
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={busy}
                          onClick={() => setConfirmSub(null)}
                        >
                          Keep
                        </Button>
                      </span>
                    ) : (
                      <Button variant="outline-danger" size="sm" disabled={busy} onClick={() => setConfirmSub(a.sub)}>
                        Remove
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <h3 className="section-label">Billing role</h3>
            {rolesError ? (
              <p className="billing-admins__warn">{`The people who hold the Billing role could not be read: ${rolesError}`}</p>
            ) : roleHolders.length === 0 ? (
              <p className="billing-admins__empty">
                Nobody holds the Billing role. It is given on the client&apos;s Users page.
              </p>
            ) : (
              <ul className="billing-admins__list">
                {roleHolders.map((h) => (
                  <li key={h.sub}>
                    <span className="billing-admins__who">{emailOf(h.sub)}</span>
                    <span className="billing-admins__note">
                      {adminSubs.has(h.sub) ? 'Also has permanent access' : 'Given on the Users page'}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <Form.Label htmlFor="billing-admins-add">Give a person permanent access</Form.Label>
            <InputGroup>
              <Form.Select
                id="billing-admins-add"
                value={selectedSub}
                onChange={(e) => setSelectedSub(e.target.value)}
                disabled={busy || candidates.length === 0}
              >
                <option value="">
                  {candidates.length === 0
                    ? usersError
                      ? "The client's users did not load"
                      : 'Nobody left to add'
                    : 'Choose a person'}
                </option>
                {candidates.map((u) => (
                  <option key={u.sub} value={u.sub}>
                    {u.email}
                  </option>
                ))}
              </Form.Select>
              <Button variant="outline-secondary" disabled={busy || !selectedSub} onClick={promote}>
                Give access
              </Button>
            </InputGroup>
            {usersError && (
              <p className="billing-admins__warn billing-admins__warn--after">{`The client's users did not load: ${usersError}`}</p>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}
